import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  ChevronLeft,
  Receipt,
  Calendar,
  IndianRupee,
  FileImage,
  File as FilePdf,
  FileArchive,
  Clock,
  Eye,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  FileX
} from 'lucide-react';

interface CustomerBill {
  _id: string;
  invoiceNumber?: string;
  amount?: number;
  purchaseDate?: string;
  fileUrl: string;
  fileName: string;
  fileType: string;
  fileSize?: number;
  notes?: string;
  createdAt: string;
  downloadCount: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

const MyBills: React.FC = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState<CustomerBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [stats, setStats] = useState({ totalBills: 0, totalDownloads: 0, recentBills: 0 });

  useEffect(() => {
    fetchBills();
    fetchStats();
  }, []);

  const fetchBills = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/customer-bills/my-bills`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBills(data.data);
      } else {
        setError(data.message || 'Failed to fetch bills');
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      setError('Failed to load your bills. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/customer-bills/stats`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDownload = async (billId: string, fileUrl: string, fileName: string) => {
    try {
      setDownloadingId(billId);
      const token = localStorage.getItem('token');
      
      // First, notify backend about the download
      const response = await fetch(`${API_URL}/customer-bills/${billId}/download`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = data.data.fileUrl.startsWith('http')
          ? data.data.fileUrl
          : `${SERVER_URL}${data.data.fileUrl}`;
        link.download = data.data.fileName || fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Update stats
        fetchStats();
      } else {
        alert(data.message || 'Failed to download bill');
      }
    } catch (error) {
      console.error('Error downloading bill:', error);
      alert('Failed to download bill. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount: number | undefined) => {
    if (!amount) return 'Not specified';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getFileIcon = (fileType: string) => {
    if (fileType === 'pdf') {
      return (
        <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center">
          <FilePdf className="w-7 h-7 text-red-600" />
        </div>
      );
    }
    if (['jpg', 'jpeg', 'png', 'webp'].includes(fileType)) {
      return (
        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
          <FileImage className="w-7 h-7 text-blue-600" />
        </div>
      );
    }
    return (
      <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center">
        <FileArchive className="w-7 h-7 text-gray-600" />
      </div>
    );
  };

  const formatFileSize = (size?: number) => {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)} 
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Go back"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">My Bills & Invoices</h1>
                <p className="text-sm text-gray-500">View and download your purchase bills</p>
              </div>
            </div>
            <button
              onClick={fetchBills}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Bills</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalBills}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Download className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Downloads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalDownloads}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Recent Bills (30 days)</p>
                <p className="text-2xl font-bold text-gray-900">{stats.recentBills}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={fetchBills}
              className="ml-auto text-red-600 hover:text-red-700 font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Bills List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-500">Loading your bills...</p>
            </div>
          ) : bills.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileX className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Bills Found</h3>
              <p className="text-gray-500 max-w-md mx-auto mb-6">
                You don't have any bills uploaded yet. Bills will appear here when our admin uploads your purchase invoices.
              </p>
              <button
                onClick={fetchBills}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          ) : (
            bills.map((bill) => (
              <div
                key={bill._id}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* File Icon */}
                  {getFileIcon(bill.fileType)}

                  {/* Bill Info */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {bill.invoiceNumber || 'Invoice'}
                      </h3>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        Available
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      {bill.amount && (
                        <span className="flex items-center gap-1">
                          <IndianRupee className="w-4 h-4" />
                          {formatAmount(bill.amount)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Uploaded: {formatDate(bill.createdAt)}
                      </span>
                      {bill.purchaseDate && (
                        <span className="flex items-center gap-1">
                          <Receipt className="w-4 h-4" />
                          Purchase: {formatDate(bill.purchaseDate)}
                        </span>
                      )}
                      {bill.fileSize && (
                        <span className="text-gray-400">
                          {formatFileSize(bill.fileSize)}
                        </span>
                      )}
                    </div>

                    {bill.notes && (
                      <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                        {bill.notes}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <a
                      href={`http://localhost:5001${bill.fileUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </a>
                    <button
                      onClick={() => handleDownload(bill._id, bill.fileUrl, bill.fileName)}
                      disabled={downloadingId === bill._id}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {downloadingId === bill._id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          Download
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Download Stats */}
                {bill.downloadCount > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-sm text-gray-500">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Downloaded {bill.downloadCount} time{bill.downloadCount > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MyBills;
